import './styles/EventList.scss';

import { useMutation, useQuery } from '@apollo/client';
import { Cancel, Delete, Edit } from '@mui/icons-material';
import { Grid } from '@mui/material';
import { GridActionsCellItem, GridRowParams } from '@mui/x-data-grid';
import { Event, ResolvedEvent } from '@thro-types/events';

import { useCallback, useMemo, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import CANCEL_MUTATION from 'gql/mutations/EventService/cancelEvent.gql';
import DELETE_MUTATION from 'gql/mutations/EventService/deleteEvent.gql';
import QUERY from 'gql/queries/EventService/getEvents.gql';

import {
  CreateButton,
  ErrorBoundary,
  GQLGuard,
  Filter,
  TilesOrListView,
  TypedGridColumns,
  ConfirmationDialog
} from 'app/components';
import { MobileFilter } from 'app/components/MobileFilter';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import { SortOption } from 'types/AppTypes';
import { formatToDateWithoutTime } from 'utils/dates';
import { useAuthContext } from 'utils/hooks/useAuthContext';
import { useCurrentURL } from 'utils/hooks/useCurrentURL';
import usePageHeading from 'utils/hooks/usePageHeading';

import { EventCard } from '../EventCard';
import { EventModal } from '../EventModal';
import { InfoDialog } from '../components/InfoDialog';
import { isEventActive, isOrganizer } from '../helper';

interface Props {}

const numberOfEventsShown = 12;

export function EventList(props: Props) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentURL = useCurrentURL();
  const { user: currentUser, authorRoles } = useAuthContext();

  const titleInputState = useState('');
  const hostInputState = useState('');
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [disabledCancel, setDisabledCancel] = useState(false);
  const [eventRef, setEventRef] = useState<ResolvedEvent | undefined>(undefined);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [createInfoDialogOpen, setCreateInfoDialogOpen] = useState(false);

  const getEventsQuery = useQuery<{ getEvents: { data: ResolvedEvent[] } }, any>(QUERY, {
    context: { clientName: 'federation' },
    variables: {
      filter: { cancelled: false, active: true },
      pagination: { limit: numberOfEventsShown, offset: 0 },
      sortings: { createdAt: 'desc' }
    },
    fetchPolicy: 'network-only'
  });
  const { fetchMore, refetch } = getEventsQuery;

  const [deleteEvent] = useMutation(DELETE_MUTATION, {
    context: { clientName: 'federation' }
  });

  const [cancelEvent] = useMutation(CANCEL_MUTATION, {
    context: { clientName: 'federation' }
  });

  usePageHeading(t(translations.pages.events.title()));

  let ableToCreate = false;
  if (authorRoles?.length > 0) {
    ableToCreate = true;
  }

  const cancelEventCreation = (event?: undefined, reason?: string | undefined) => {
    if (reason && reason === 'backdropClick') return;
    if (disabledCancel) {
      return;
    }
    setDisabledCancel(true);
    setFormModalOpen(false);
    setDisabledCancel(false);
  };

  const handleEventDeletion = async (event: Event | undefined) => {
    try {
      if (event) {
        await deleteEvent({ variables: { id: event.id }, refetchQueries: ['GetEvents'] });
        dispatch(AlertActions.success(t(translations.pages.events.alerts.successfulDelete())));
      }
    } catch (err) {
      dispatch(AlertActions.error(t(translations.pages.events.alerts.failedDelete())));
    }
  };

  const handleEventCancelation = async (event: Event | undefined) => {
    try {
      if (event) {
        await cancelEvent({ variables: { id: event.id }, refetchQueries: ['GetEvents'] });
        dispatch(AlertActions.success(t(translations.pages.events.alerts.successfulCancel())));
      }
    } catch (err) {
      dispatch(AlertActions.error(t(translations.pages.events.alerts.failedCancel())));
    }
  };

  const handleCreateClick = () => {
    setEventRef(undefined);
    setCreateInfoDialogOpen(true);
  };

  const confirmCreateInfo = () => {
    setCreateInfoDialogOpen(false);
    setFormModalOpen(true);
  };

  const filters: Filter[] = [
    {
      field: 'title',
      label: t(translations.common.title()),
      stateHandler: titleInputState
    },
    {
      field: 'host',
      label: t(translations.pages.events.filter.host()),
      stateHandler: hostInputState
    },
    {
      field: 'enrolled',
      label: t(translations.pages.events.filter.enrolled()),
      toggle: {
        value: 'ENROLLED',
        color: 'default'
      }
    },
    {
      field: 'showAll',
      label: t(translations.pages.events.filter.showAll()),
      toggle: {
        value: 'ALL',
        tooltip: t(translations.pages.events.tooltips.showAll()),
        color: 'default'
      },
      hide: !authorRoles.length
    },
    {
      field: 'showMine',
      label: t(translations.pages.events.filter.myEvents()),
      toggle: {
        value: 'MINE',
        tooltip: t(translations.pages.events.tooltips.myEvents()),
        color: 'default'
      },
      hide: !authorRoles.length
    }
  ];

  const sortOptions: SortOption[] = [
    {
      label: t(translations.pages.events.sortOptions.newest()),
      value: { createdAt: 'desc' }
    },
    {
      label: t(translations.pages.events.sortOptions.startDate()),
      value: { startDate: 'asc' }
    },
    {
      label: t(translations.pages.events.sortOptions.title()),
      value: { title: 'asc' }
    },
    {
      label: t(translations.pages.events.sortOptions.host()),
      value: { host: 'asc' }
    }
  ];

  type parsedJSON = { exact: boolean; term: string };

  interface Parsedfilter {
    title: parsedJSON;
    host: parsedJSON;
    enrolled: Boolean;
    showAll: Boolean;
    showMine: Boolean;
  }

  const organizerActions = useCallback(
    params => {
      const isActive = params.row.occupancy > 0;
      return [
        <GridActionsCellItem
          icon={<Edit sx={{ color: 'lightblue' }} />}
          label={t(translations.common.edit())}
          onClick={() => {
            setEventRef(params.row);
            setFormModalOpen(true);
          }}
          showInMenu
          disabled={
            !isOrganizer(currentUser, authorRoles, params.row.organizers, true, params.row.hostId)
          }
        />,
        !isActive ? (
          <GridActionsCellItem
            icon={<Delete sx={{ color: 'red' }} />}
            label={t(translations.common.delete())}
            onClick={() => {
              setEventRef(params.row);
              setDeleteModalOpen(true);
            }}
            showInMenu
            disabled={
              !isOrganizer(
                currentUser,
                authorRoles,
                params.row.organizers,
                true,
                params.row.hostId
              ) && !(params.row.occupancy > 0)
            }
          />
        ) : (
          <GridActionsCellItem
            icon={<Cancel sx={{ color: 'lightred' }} />}
            label={t(translations.common.cancelEvent())}
            onClick={() => {
              setEventRef(params.row);
              setCancelModalOpen(true);
            }}
            showInMenu
            disabled={
              !isOrganizer(
                currentUser,
                authorRoles,
                params.row.organizers,
                true,
                params.row.hostId
              ) && params.row.occupancy > 0
            }
          />
        )
      ];
    },
    [authorRoles, currentUser, t]
  );

  const rowActions = useCallback(
    (params: GridRowParams<ResolvedEvent>) => {
      const action: JSX.Element[] = [];
      if (isEventActive(params.row)) {
        action.push(...organizerActions(params));
      }
      return action;
    },
    [organizerActions]
  );

  const columns = useMemo((): TypedGridColumns<ResolvedEvent> => {
    let columns: TypedGridColumns<ResolvedEvent> = [
      {
        field: 'name',
        flex: 3,
        minWidth: 200,
        headerName: t(translations.pages.events.create.eventName())
      },
      {
        field: 'host',
        flex: 3,
        minWidth: 200,
        headerName: t(translations.pages.events.create.host())
      },
      {
        field: 'startDate',
        flex: 1,
        minWidth: 200,
        headerName: t(translations.pages.events.startDate()),
        valueFormatter: value => {
          return formatToDateWithoutTime(value);
        }
      },
      {
        field: 'endDate',
        flex: 1,
        minWidth: 200,
        headerName: t(translations.pages.events.endDate()),
        valueFormatter: value => {
          return formatToDateWithoutTime(value);
        }
      }
    ];
    if (authorRoles?.length > 0)
      columns.push({
        field: 'actions',
        flex: 1,
        minWidth: 100,
        headerName: t(translations.common.actions()),
        type: 'actions',
        getActions: rowActions
      });
    return columns;
  }, [authorRoles, rowActions, t]);

  return (
    <>
      <InfoDialog
        dialogOpen={createInfoDialogOpen}
        handleConfirm={confirmCreateInfo}
        handleClose={() => setCreateInfoDialogOpen(false)}
      />
      <ErrorBoundary boundaryTag="eventDetailBoundary">
        <EventModal
          event={eventRef}
          createModalOpen={formModalOpen}
          onCancel={() => {
            cancelEventCreation();
          }}
          disabledCancel={disabledCancel}
          closeHandler={() => setFormModalOpen(false)}
        />
      </ErrorBoundary>
      <ConfirmationDialog
        open={deleteModalOpen}
        title={t(translations.pages.events.confirmationDialog.deleteEventWarning())}
        onClose={() => {
          setDeleteModalOpen(false);
        }}
        onConfirm={() => {
          handleEventDeletion(eventRef);
          setDeleteModalOpen(false);
        }}
      >
        <div>{t(translations.pages.events.confirmationDialog.deleteEventInformation())}</div>
      </ConfirmationDialog>
      <ConfirmationDialog
        open={cancelModalOpen}
        title={t(translations.pages.events.confirmationDialog.cancelEventWarning())}
        onClose={() => {
          setCancelModalOpen(false);
        }}
        onConfirm={() => {
          handleEventCancelation(eventRef);
          setCancelModalOpen(false);
        }}
      >
        <div>{t(translations.pages.events.confirmationDialog.cancelEventInformation())}</div>
      </ConfirmationDialog>
      <GQLGuard query={getEventsQuery}>
        {data => {
          const events: ResolvedEvent[] = data.getEvents.data;
          const applyFilter = (filters: {
            title?: string;
            host?: string;
            enrolled?: boolean;
            showMine?: boolean;
            showAll?: boolean;
          }) => {
            const showAll: {
              cancelled?: boolean;
              active?: boolean;
            } = { cancelled: false, active: true };
            if (filters.showAll) {
              showAll.active = undefined;
              showAll.cancelled = undefined;
            }
            delete filters.showAll;
            return refetch({ filter: { ...filters, ...showAll } });
          };

          return (
            <div style={{ paddingBottom: isMobile ? 100 : undefined }}>
              {isMobile && <MobileFilter onApply={applyFilter} />}
              <TilesOrListView
                data={events}
                columns={columns}
                height={'70vh'}
                filters={filters}
                onFilterChange={(values: { filter: string }) => {
                  const filter: Parsedfilter = JSON.parse(values.filter);
                  const title = filter.title ? filter.title.term : null;
                  const host = filter.host ? filter.host.term : null;
                  const enrolled = filter.enrolled ? true : null;
                  const showMine = filter.showMine ? true : undefined;
                  const showAll: {
                    cancelled?: boolean;
                    active?: boolean;
                  } = { cancelled: false, active: true };
                  if (filter.showAll) {
                    showAll.active = undefined;
                    showAll.cancelled = undefined;
                  }
                  return refetch({ filter: { title, host, enrolled, showMine, ...showAll } });
                }}
                renderTile={event => (
                  <EventCard
                    event={event}
                    isPreview={false}
                    setEventRef={setEventRef}
                    setFormModalOpen={setFormModalOpen}
                    setDeleteModalOpen={setDeleteModalOpen}
                    setCancelModalOpen={setCancelModalOpen}
                  />
                )}
                onRowClick={event => {
                  navigate(`/events/${event.row.id}`, { state: { back: currentURL } });
                }}
                controlElements={
                  ableToCreate && (
                    <Grid item>
                      <CreateButton onClick={handleCreateClick}>
                        {t(translations.common.create())}
                      </CreateButton>
                    </Grid>
                  )
                }
                sortOptions={sortOptions}
                onSortChange={sortings => {
                  refetch({ sortings });
                }}
                fetchMore={fetchMore}
                numberOfTilesShown={numberOfEventsShown}
                getRowClassName={params => {
                  return isEventActive(params.row) ? '' : 'event-inactive';
                }}
              />
            </div>
          );
        }}
      </GQLGuard>
    </>
  );
}
